import { useState } from 'react';
import { 
  Plus, Minus, Trash2, ShoppingCart, CreditCard, 
  ParkingCircle, AlertTriangle, User, MessageSquare, X, Split, Ban
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { Label } from '../../../components/ui/label';
import { Badge } from '../../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import VoidModal from './VoidModal';

export default function OrderPanel({
  order,
  setOrder,
  onUpdateQuantity,
  onRemoveItem,
  onUpdateNotes,
  onClear,
  onPark,
  onPay,
  onSplit,
  onVoidItem,
  subtotal,
  orderId
}) {
  const [editingNotes, setEditingNotes] = useState(null);
  const [tempNotes, setTempNotes] = useState('');
  const [voidingItem, setVoidingItem] = useState(null);
  const [voidingIndex, setVoidingIndex] = useState(null);

  const hasItems = order.items.length > 0;

  // Check for allergy keywords in notes
  const hasAllergyNote = (notes) => {
    if (!notes) return false;
    const allergyKeywords = ['allergy', 'allergic', 'no ', 'without', 'gluten', 'dairy', 'nut', 'peanut', 'shellfish'];
    return allergyKeywords.some(keyword => notes.toLowerCase().includes(keyword));
  };

  // Handle void completion
  const handleVoidComplete = (itemIndex, quantity, voidData) => {
    if (onVoidItem) {
      onVoidItem(itemIndex, quantity, voidData);
    } else {
      // Local void (for new orders not yet saved)
      setOrder(prev => {
        const newItems = [...prev.items];
        if (quantity >= newItems[itemIndex].quantity) {
          // Remove entire item
          newItems.splice(itemIndex, 1);
        } else {
          // Reduce quantity
          newItems[itemIndex] = {
            ...newItems[itemIndex],
            quantity: newItems[itemIndex].quantity - quantity
          };
        }
        return { ...prev, items: newItems };
      });
    }
    setVoidingItem(null);
    setVoidingIndex(null);
  };

  const openNotesDialog = (index, currentNotes) => {
    setEditingNotes(index);
    setTempNotes(currentNotes || '');
  };

  const saveNotes = () => {
    if (editingNotes !== null) {
      onUpdateNotes(editingNotes, tempNotes);
      setEditingNotes(null);
      setTempNotes('');
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Order Header */}
      <div className="p-4 border-b border-slate-200">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <ShoppingCart className="w-5 h-5 text-slate-600" />
            <h2 className="font-semibold text-slate-900">Current Order</h2>
          </div>
          {hasItems && (
            <Button
              variant="ghost"
              size="sm"
              className="text-red-500 hover:text-red-600 hover:bg-red-50"
              onClick={onClear}
              data-testid="clear-order-btn"
            >
              <Trash2 className="w-4 h-4 mr-1" />
              Clear
            </Button>
          )}
        </div>

        {/* Customer Info */}
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-xs text-slate-500">Customer</Label>
            <Input
              placeholder="Name"
              value={order.customer_name}
              onChange={(e) => setOrder({ ...order, customer_name: e.target.value })}
              className="h-8 text-sm"
              data-testid="customer-name-input"
            />
          </div>
          <div>
            <Label className="text-xs text-slate-500">Table</Label>
            <Input
              placeholder="Table #"
              value={order.table_number}
              onChange={(e) => setOrder({ ...order, table_number: e.target.value })}
              className="h-8 text-sm"
              data-testid="table-number-input"
            />
          </div>
        </div>
      </div>

      {/* Order Items */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {!hasItems ? (
          <div className="text-center py-12">
            <ShoppingCart className="w-12 h-12 text-slate-200 mx-auto mb-3" />
            <p className="text-slate-400">No items yet</p>
            <p className="text-sm text-slate-300">Tap items to add them</p>
          </div>
        ) : (
          order.items.map((item, index) => (
            <div 
              key={index} 
              className="bg-slate-50 rounded-lg p-3"
              data-testid={`order-item-${index}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <h4 className="font-medium text-slate-900 text-sm">{item.name}</h4>
                  <p className="text-xs text-slate-500">{item.category}</p>
                </div>
                <span className="font-semibold text-slate-900">
                  £{(item.price * item.quantity).toFixed(2)}
                </span>
              </div>

              {/* Item Notes */}
              {item.notes && (
                <div className={`mt-2 p-2 rounded text-sm ${
                  hasAllergyNote(item.notes) 
                    ? 'bg-red-100 text-red-800 border border-red-300' 
                    : 'bg-yellow-50 text-yellow-800'
                }`}>
                  {hasAllergyNote(item.notes) && (
                    <AlertTriangle className="w-4 h-4 inline mr-1" />
                  )}
                  <span className="font-medium">{item.notes}</span>
                </div>
              )}

              {/* Quantity Controls */}
              <div className="flex items-center justify-between mt-2">
                <div className="flex items-center gap-1">
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => onUpdateQuantity(index, -1)}
                    data-testid={`qty-minus-${index}`}
                  >
                    <Minus className="w-3 h-3" />
                  </Button>
                  <span className="w-8 text-center font-medium">{item.quantity}</span>
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => onUpdateQuantity(index, 1)}
                    data-testid={`qty-plus-${index}`}
                  >
                    <Plus className="w-3 h-3" />
                  </Button>
                </div>

                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => openNotesDialog(index, item.notes)}
                    data-testid={`add-note-${index}`}
                  >
                    <MessageSquare className={`w-4 h-4 ${item.notes ? 'text-orange-500' : 'text-slate-400'}`} />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-orange-500 hover:text-orange-600 hover:bg-orange-50"
                    onClick={() => {
                      setVoidingItem(item);
                      setVoidingIndex(index);
                    }}
                    data-testid={`void-item-${index}`}
                    title="Void item"
                  >
                    <Ban className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-red-500 hover:text-red-600 hover:bg-red-50"
                    onClick={() => onRemoveItem(index)}
                    data-testid={`remove-item-${index}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Order Notes */}
      {hasItems && (
        <div className="px-4 pb-2">
          <Textarea
            placeholder="Order notes (allergies, special requests)..."
            value={order.notes}
            onChange={(e) => setOrder({ ...order, notes: e.target.value })}
            className="text-sm resize-none h-16"
            data-testid="order-notes"
          />
        </div>
      )}

      {/* Order Summary */}
      <div className="p-4 border-t border-slate-200 bg-slate-50">
        <div className="space-y-2 mb-4">
          <div className="flex justify-between text-sm">
            <span className="text-slate-600">Subtotal ({order.items.length} items)</span>
            <span className="font-medium">£{subtotal.toFixed(2)}</span>
          </div>
          <div className="flex justify-between text-lg font-bold">
            <span>Total</span>
            <span className="text-orange-500">£{subtotal.toFixed(2)}</span>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="grid grid-cols-3 gap-2">
          <Button
            variant="outline"
            onClick={onPark}
            disabled={!hasItems}
            className="h-12"
            data-testid="park-order-btn"
          >
            <ParkingCircle className="w-4 h-4 mr-2" />
            Park
          </Button>
          <Button
            variant="outline"
            onClick={onSplit}
            disabled={!hasItems}
            className="h-12"
            data-testid="split-bill-btn"
          >
            <Split className="w-4 h-4 mr-2" />
            Split
          </Button>
          <Button
            className="h-12 bg-green-600 hover:bg-green-700"
            onClick={onPay}
            disabled={!hasItems}
            data-testid="pay-btn"
          >
            <CreditCard className="w-4 h-4 mr-2" />
            Pay
          </Button>
        </div>
      </div>

      {/* Notes Dialog */}
      <Dialog open={editingNotes !== null} onOpenChange={() => setEditingNotes(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Item Notes</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800">
              <AlertTriangle className="w-4 h-4 inline mr-2" />
              Allergy or dietary notes will be highlighted in red
            </div>
            <Textarea
              placeholder="Add notes (allergies, modifications, etc.)"
              value={tempNotes}
              onChange={(e) => setTempNotes(e.target.value)}
              rows={3}
              data-testid="item-notes-input"
            />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setEditingNotes(null)}>
                Cancel
              </Button>
              <Button onClick={saveNotes} data-testid="save-notes-btn">
                Save Notes
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Void Modal */}
      <VoidModal
        open={voidingItem !== null}
        onClose={() => {
          setVoidingItem(null);
          setVoidingIndex(null);
        }}
        item={voidingItem}
        itemIndex={voidingIndex}
        orderId={orderId}
        onVoidComplete={handleVoidComplete}
      />
    </div>
  );
}
